// Simple Sponsor Service
import { apiService } from './api';

export interface Sponsor {
  id: string;
  program: string;
  name: string;
  email: string;
  user_id?: string;
  confirmation_status?: 'confirmed' | 'unconfirmed';
  auto_share_enabled?: boolean;
  share_link_token?: string;
  created_at?: string;
}

export interface Sponsee {
  id: string;
  program: string;
  name: string;
  email: string;
  user_id?: string;
  confirmation_status?: 'confirmed' | 'unconfirmed';
  auto_share_enabled?: boolean;
  share_link_token?: string;
  created_at?: string;
}

export interface CreateSponsorRequest {
  program: string;
  name: string;
  email: string;
}

export interface Invitation {
  invitation_id: string;
  from_name: string;
  from_email: string;
  relationship_type: 'sponsor' | 'sponsee' | 'friend';
  message: string;
  program?: string;
  created_at: string;
}

export interface SentInvitation {
  invitation_id: string;
  to_email: string;
  relationship_type: 'sponsor' | 'sponsee' | 'friend';
  message: string;
  program?: string;
  status: 'pending' | 'accept' | 'decline';
  created_at: string;
  accepted_at?: string;
}

export interface InvitationRequest {
  to_email: string;
  relationship_type: 'sponsor' | 'sponsee' | 'friend';
  message?: string;
  program?: string;
}

export interface SponseeProgress {
  sponsee: {
    name: string;
    email: string;
  };
  progress: {
    steps: Record<string, any>;
    inventories: {
      resentments: number;
      fears: number;
      harms: number;
    };
  };
}

class SponsorService {
  /**
   * Get user's sponsors
   */
  static async getSponsors(): Promise<Sponsor[]> {
    try {
      const response = await apiService.getSponsors();
      return response.sponsors || [];
    } catch (error) {
      console.error('Error getting sponsors:', error);
      throw error;
    }
  }

  /**
   * Add a new sponsor
   */
  static async createSponsor(request: CreateSponsorRequest): Promise<Sponsor> {
    try {
      const response = await apiService.createSponsor(request);
      return response.sponsor;
    } catch (error) {
      console.error('Error creating sponsor:', error);
      throw error;
    }
  }

  /**
   * Delete a sponsor
   */
  static async deleteSponsor(sponsorId: string): Promise<void> {
    try {
      await apiService.deleteSponsor(sponsorId);
    } catch (error) {
      console.error('Error deleting sponsor:', error);
      throw error;
    }
  }

  /**
   * Get user's sponsees (people they sponsor)
   */
  static async getSponsees(): Promise<Sponsee[]> {
    try {
      const response = await apiService.getSponsees();
      return response.sponsees || [];
    } catch (error) {
      console.error('Error getting sponsees:', error);
      throw error;
    }
  }

  /**
   * Add a new sponsee
   */
  static async createSponsee(request: CreateSponsorRequest): Promise<Sponsee> {
    try {
      const response = await apiService.createSponsee(request);
      return response.sponsee;
    } catch (error) {
      console.error('Error creating sponsee:', error);
      throw error;
    }
  }

  /**
   * Delete a sponsee
   */
  static async deleteSponsee(sponseeId: string): Promise<void> {
    try {
      await apiService.deleteSponsee(sponseeId);
    } catch (error) {
      console.error('Error deleting sponsee:', error);
      throw error;
    }
  }

  /**
   * Send an invitation
   */
  static async sendInvitation(request: InvitationRequest): Promise<void> {
    try {
      await apiService.sendInvitation(request);
    } catch (error) {
      console.error('Error sending invitation:', error);
      throw error;
    }
  }

  /**
   * Get pending invitations for current user (received)
   */
  static async getPendingInvitations(): Promise<Invitation[]> {
    try {
      const response = await apiService.getPendingInvitations();
      return response.invitations || [];
    } catch (error) {
      console.error('Error getting pending invitations:', error);
      throw error;
    }
  }

  /**
   * Get invitations sent by current user
   */
  static async getSentInvitations(): Promise<SentInvitation[]> {
    try {
      const response = await apiService.getSentInvitations();
      return response.invitations || [];
    } catch (error) {
      console.error('Error getting sent invitations:', error);
      throw error;
    }
  }

  /**
   * Get invitation by token (for signup flow)
   */
  static async getInvitationByToken(token: string): Promise<any> {
    try {
      const response = await apiService.getInvitationByToken(token);
      return response;
    } catch (error) {
      console.error('Error getting invitation by token:', error);
      throw error;
    }
  }

  /**
   * Respond to an invitation
   */
  static async respondToInvitation(invitationId: string, response: 'accept' | 'decline'): Promise<void> {
    try {
      await apiService.respondToInvitation(invitationId, response);
    } catch (error) {
      console.error('Error responding to invitation:', error);
      throw error;
    }
  }

  /**
   * Toggle auto-share with a sponsor
   */
  static async toggleAutoShare(sponsorId: string, enabled: boolean): Promise<any> {
    try {
      const response = await apiService.toggleAutoShare(sponsorId, enabled);
      return response;
    } catch (error) {
      console.error('Error toggling auto-share:', error);
      throw error;
    }
  }

  /**
   * Get sponsee's progress via share token
   */
  static async getSponseeProgress(shareToken: string): Promise<SponseeProgress> {
    try {
      const response = await apiService.getSponseeProgress(shareToken);
      return response;
    } catch (error) {
      console.error('Error getting sponsee progress:', error);
      throw error;
    }
  }
}

export { SponsorService };

